import type {
  Location,
  ReputationOverviewDto,
  ReputationSummaryDto,
} from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
  ghostAccentIconTileClass,
  healthAttentionSurfaceClass,
  healthCriticalSurfaceClass,
  healthOkSurfaceClass,
} from '@up2stay/up2promo-ui';
import { ChevronDown, Link2, MapPin, Package, Star } from 'lucide-react';
import { useState } from 'react';

import {
  PlanLockBadge,
  UpgradeCard,
} from '@/core/components/billing/upgrade-surfaces';
import {
  SettingsGroup,
  SettingsGroupFooter,
  SettingsGroupLabel,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useIsMobile } from '@/core/hooks/use-mobile';
import type { LocationConnectionStatus } from '@/core/lib/review-channel-setup';

import type { LocationDetailDict } from './location-detail-types';
import { LOCATION_DETAIL_MUTED_CLASS } from './location-detail-section-card';
import type {
  LocationHealthScope,
  LocationHealthSummary,
} from './location-health';
import { GoogleConnectionBadge } from './location-health-indicators';
import { LocationMapPreview } from './location-map-preview';
import {
  LocationReviewInsights,
  type LocationReviewInsightsLabels,
} from './location-review-insights';

export type LocationOverviewTab =
  | 'integrations'
  | 'external-services'
  | 'information';

export type OverviewHealthLabels = {
  title: string;
  googleLabel: string;
  linksLabel: string;
  productsLabel: string;
  linksOk: string;
  linksEmpty: string;
  productsCount: string;
  productsNone: string;
  googleLinked: string;
  googleListing: string;
  googleNeedsAction: string;
  googleOptOut: string;
  googleSyncFailed: string;
  ctaLinkGoogle: string;
  ctaFixSync: string;
  ctaAddLinks: string;
  ctaViewProducts: string;
  ctaEditInfo: string;
  allClear: string;
};

export type PrimaryAction =
  | { kind: 'tab'; tab: LocationOverviewTab; label: string }
  | { kind: 'products'; label: string };

/**
 * Repair CTA for triage continuity — Google sync gaps and empty links only.
 * Products stay as status meta, not a Signal Red interrupt.
 */
export function resolvePrimaryAction(
  health: LocationHealthSummary,
  _linkedProductCount: number,
  labels: OverviewHealthLabels,
  scope?: LocationHealthScope,
): PrimaryAction | null {
  if (scope?.includeReputation !== false) {
    if (health.syncFailed) {
      return { kind: 'tab', tab: 'integrations', label: labels.ctaFixSync };
    }
    if (health.google === 'needs_action') {
      return { kind: 'tab', tab: 'integrations', label: labels.ctaLinkGoogle };
    }
  }
  if (health.links === 'empty') {
    return { kind: 'tab', tab: 'external-services', label: labels.ctaAddLinks };
  }
  return null;
}

function healthCardSurfaceClass(
  health: LocationHealthSummary,
  scope?: LocationHealthScope,
): string {
  if (
    scope?.includeReputation !== false &&
    (health.syncFailed || health.google === 'needs_action')
  ) {
    return healthCriticalSurfaceClass;
  }
  if (health.links === 'empty') {
    return healthAttentionSurfaceClass;
  }
  return healthOkSurfaceClass;
}

export function LocationDetailOverviewTab({
  location,
  health,
  linkedProductCount,
  onNavigateTab,
  onViewProducts,
  dict,
  healthLabels,
  reviewInsights,
}: {
  location: Location;
  health: LocationHealthSummary;
  linkedProductCount: number;
  onNavigateTab: (tab: LocationOverviewTab) => void;
  onViewProducts: () => void;
  dict?: LocationDetailDict;
  healthLabels: OverviewHealthLabels;
  /** When set, Overview leads with reputation / KI summary (Status Board). */
  reviewInsights?: {
    enabled: boolean;
    overview: ReputationOverviewDto | undefined;
    isLoading: boolean;
    summary: ReputationSummaryDto | undefined;
    summaryLoading: boolean;
    summaryError: boolean;
    locale: string;
    labels: LocationReviewInsightsLabels;
    onOpenReputation: () => void;
    onOpenInbox: () => void;
  };
}) {
  const { isFeatureEnabled } = useEntitlements();
  const isMobile = useIsMobile();
  const includeReputation = isFeatureEnabled('feature.reputation');
  const healthScope = { includeReputation };

  const hasCoordinates =
    location.coordinates &&
    typeof location.coordinates?.latitude === 'number' &&
    typeof location.coordinates?.longitude === 'number';
  const lat = location.coordinates?.latitude;
  const lng = location.coordinates?.longitude;

  const showInsights = !!reviewInsights?.enabled;
  /** Reputation signal owns the hero — map stays secondary / collapsed. */
  const [mapOpen, setMapOpen] = useState(!showInsights);

  const primary = resolvePrimaryAction(
    health,
    linkedProductCount,
    healthLabels,
    healthScope,
  );

  const googleStatus: LocationConnectionStatus = health.google;

  const mapSection =
    hasCoordinates && lat != null && lng != null ? (
      <Collapsible open={mapOpen} onOpenChange={setMapOpen}>
        <SettingsGroup>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:px-5"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span
                  className={cn(
                    'flex size-8 shrink-0 items-center justify-center rounded-[var(--r-control)]',
                    ghostAccentIconTileClass,
                  )}
                  aria-hidden
                >
                  <MapPin className="size-3.5" />
                </span>
                <span className={cn('truncate', LOCATION_DETAIL_MUTED_CLASS)}>
                  {dict?.mapPreviewLabel ?? 'Map'}
                  {location.name ? ` · ${location.name}` : ''}
                </span>
              </span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out motion-reduce:transition-none',
                  mapOpen && 'rotate-180',
                )}
                aria-hidden
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="border-t border-border">
              <LocationMapPreview
                latitude={lat}
                longitude={lng}
                name={location.name}
                mapPreviewLabel={dict?.mapPreviewLabel}
                noCoordinatesLabel={dict?.noCoordinatesLabel}
                mapUnavailableLabel={dict?.mapUnavailableLabel}
                compact
                bare
              />
            </div>
          </CollapsibleContent>
        </SettingsGroup>
      </Collapsible>
    ) : null;

  return (
    <div className="flex w-full min-w-0 flex-col gap-6">
      {!includeReputation ? (
        <UpgradeCard feature="feature.reputation" compact />
      ) : null}

      {showInsights && reviewInsights ? (
        <LocationReviewInsights
          connectionStatus={googleStatus}
          overview={reviewInsights.overview}
          isLoading={reviewInsights.isLoading}
          summary={reviewInsights.summary}
          summaryLoading={reviewInsights.summaryLoading}
          summaryError={reviewInsights.summaryError}
          locale={reviewInsights.locale}
          labels={reviewInsights.labels}
          onOpenReputation={reviewInsights.onOpenReputation}
          onOpenInbox={reviewInsights.onOpenInbox}
          onConnectSync={() => onNavigateTab('integrations')}
        />
      ) : null}

      <div className="flex min-w-0 flex-col gap-2">
        <SettingsGroupLabel>{healthLabels.title}</SettingsGroupLabel>
        <SettingsGroup
          className={cn(
            'min-w-0 w-full',
            healthCardSurfaceClass(health, healthScope),
          )}
        >
          <SettingsGroupRows>
            <SettingsGroupRow>
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <Star className="size-4 shrink-0 text-muted-foreground" />
                  <span className="text-[13.5px] font-medium leading-relaxed text-foreground">
                    {healthLabels.googleLabel}
                  </span>
                </div>
                {includeReputation ? (
                  health.syncFailed ? (
                    <Badge variant="destructive">
                      {healthLabels.googleSyncFailed}
                    </Badge>
                  ) : (
                    <GoogleConnectionBadge
                      status={googleStatus}
                      labels={{
                        googleLinked: healthLabels.googleLinked,
                        googleListing: healthLabels.googleListing,
                        googleNeedsAction: healthLabels.googleNeedsAction,
                        googleOptOut: healthLabels.googleOptOut,
                      }}
                    />
                  )
                ) : (
                  <PlanLockBadge feature="feature.reputation" />
                )}
              </div>
            </SettingsGroupRow>
            <SettingsGroupRow separated>
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <Link2 className="size-4 shrink-0 text-muted-foreground" />
                  <span className="text-[13.5px] font-medium leading-relaxed text-foreground">
                    {healthLabels.linksLabel}
                  </span>
                </div>
                <Badge
                  variant={health.links === 'ok' ? 'success' : 'destructive'}
                >
                  {health.links === 'ok'
                    ? healthLabels.linksOk
                    : healthLabels.linksEmpty}
                </Badge>
              </div>
            </SettingsGroupRow>
            <SettingsGroupRow separated>
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <Package className="size-4 shrink-0 text-muted-foreground" />
                  <span className="text-[13.5px] font-medium leading-relaxed text-foreground">
                    {healthLabels.productsLabel}
                  </span>
                </div>
                <Badge variant="outline">
                  {linkedProductCount > 0
                    ? healthLabels.productsCount.replace(
                        '{count}',
                        String(linkedProductCount),
                      )
                    : healthLabels.productsNone}
                </Badge>
              </div>
            </SettingsGroupRow>
            <SettingsGroupRow separated>
              {primary ? (
                <Button
                  className={isMobile ? 'w-full' : 'w-full sm:w-auto'}
                  onClick={() => {
                    if (primary.kind === 'products') {
                      onViewProducts();
                      return;
                    }
                    onNavigateTab(primary.tab);
                  }}
                >
                  {primary.label}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className={isMobile ? 'w-full' : 'w-full sm:w-auto'}
                  onClick={() => onNavigateTab('information')}
                >
                  {healthLabels.ctaEditInfo}
                </Button>
              )}
            </SettingsGroupRow>
          </SettingsGroupRows>
        </SettingsGroup>
        {!primary ? (
          <SettingsGroupFooter>{healthLabels.allClear}</SettingsGroupFooter>
        ) : null}
      </div>

      {mapSection}
    </div>
  );
}
